// Module 10 contracts: vehicles, parking, gate review, ANPR and observed vehicle movements
// (PB-10-01 … PB-10-15, ADR-015). Messages are @passage/i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const IsoDateTime = z.iso.datetime({ offset: true });
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time');
const Code = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,19}$/, 'v.parkingCode');
const GateLabel = z.string().trim().min(1, 'v.required').max(80, 'v.tooLong');
export const Jurisdiction = z.string().trim().toUpperCase().regex(/^([A-Z]{2}(-[A-Z0-9]{1,3})?|XX)$/, 'v.jurisdiction');
export const PlateText = z.string().trim().min(1, 'v.required').max(40, 'v.tooLong');

// ------------------------------------------------------------------ gate policy (PD-162)
export const PHOTO_PURPOSES = ['vehicle_condition', 'plate_evidence', 'visitor_identification'] as const;
export const GatePolicySettings = z
  .object({
    earlyArrivalMinutes: z.number().int().min(0).max(1440).optional(),
    evaluationMaxAgeSeconds: z.number().int().min(15).max(3600).optional(),
    anpr: z
      .object({
        minConfidence: z.number().min(0.5).max(1).optional(),
        dedupeWindowSeconds: z.number().int().min(0).max(3600).optional(),
        readMaxAgeSeconds: z.number().int().min(10).max(86_400).optional(),
      })
      .optional(),
    parking: z.object({ exactSpaces: z.boolean().optional(), notifyOnChange: z.boolean().optional(), overstayGraceMinutes: z.number().int().min(0).max(1440).optional() }).optional(),
    photo: z.object({ enabled: z.boolean().optional(), purposes: z.array(z.enum(PHOTO_PURPOSES)).max(3).optional(), retentionDays: z.number().int().min(1).max(365).optional() }).optional(),
    offline: z
      .object({
        procedureApproved: z.boolean().optional(),
        procedureText: z.string().trim().max(2000, 'v.tooLong').nullable().optional(),
        maxDataAgeMinutes: z.number().int().min(1).max(240).optional(),
      })
      .optional(),
    barrier: z.object({ adapter: z.enum(['none', 'simulator']).optional(), permitMaxAgeSeconds: z.number().int().min(15).max(600).optional() }).optional(),
    hostLookup: z
      .object({
        minChars: z.number().int().min(3).max(10).optional(),
        maxResults: z.number().int().min(1).max(10).optional(),
        maxPerWindow: z.number().int().min(1).max(200).optional(),
        windowMinutes: z.number().int().min(1).max(1440).optional(),
      })
      .optional(),
    jurisdictions: z.array(Jurisdiction).min(1).max(40).optional(),
  })
  .strict();
export type GatePolicySettings = z.infer<typeof GatePolicySettings>;
export const UpdateGatePolicyRequest = z.object({ expectedVersion: z.number().int().min(0), settings: GatePolicySettings });

export interface GateRulesDto {
  readonly earlyArrivalMinutes: number;
  readonly evaluationMaxAgeSeconds: number;
  readonly anpr: { readonly minConfidence: number; readonly dedupeWindowSeconds: number; readonly readMaxAgeSeconds: number };
  readonly parking: { readonly exactSpaces: boolean; readonly notifyOnChange: boolean; readonly overstayGraceMinutes: number };
  readonly photo: { readonly enabled: boolean; readonly purposes: readonly (typeof PHOTO_PURPOSES)[number][]; readonly retentionDays: number };
  readonly offline: { readonly procedureApproved: boolean; readonly procedureText: string | null; readonly maxDataAgeMinutes: number };
  readonly barrier: { readonly adapter: 'none' | 'simulator'; readonly permitMaxAgeSeconds: number };
  readonly hostLookup: { readonly minChars: number; readonly maxResults: number; readonly maxPerWindow: number; readonly windowMinutes: number };
  readonly jurisdictions: readonly string[];
}
export interface GatePolicyResponse {
  readonly siteId: string;
  /** 0 = defaults only, nothing stored yet. */
  readonly version: number;
  readonly settings: GatePolicySettings;
  readonly effective: GateRulesDto;
  readonly updatedAt: string | null;
}

// ------------------------------------------------------------------ registry (PB-10-01)
export const VehicleListQuery = z.object({ siteId: Uuid, q: z.string().trim().max(40).optional() });
export const CreateVehicleRequest = z.object({
  siteId: Uuid,
  jurisdiction: Jurisdiction,
  plate: PlateText,
  make: optionalText(40),
  model: optionalText(40),
  colour: optionalText(40),
});
export const UpdateVehicleRequest = z.object({
  expectedVersion: z.number().int().min(1),
  make: optionalText(40),
  model: optionalText(40),
  colour: optionalText(40),
  status: z.enum(['active', 'blocked', 'inactive']).optional(),
  statusReason: optionalText(500),
});
export const AddAssociationRequest = z.object({
  personId: Uuid.optional(),
  organisationId: Uuid.optional(),
  relation: z.enum(['driver', 'owner', 'company_vehicle', 'other']),
  validFrom: IsoDateTime.optional(),
  validUntil: IsoDateTime.nullable().optional(),
});
export type VehiclePresenceDto = 'no_observation' | 'observed_onsite' | 'observed_departed' | 'unknown_conflicting';
export interface VehicleAssociationDto {
  readonly id: string;
  readonly relation: 'driver' | 'owner' | 'company_vehicle' | 'other';
  readonly personName: string | null;
  readonly organisationName: string | null;
  readonly validFrom: string;
  readonly validUntil: string | null;
}
export interface VehicleResponse {
  readonly id: string;
  readonly siteId: string;
  readonly jurisdiction: string;
  /** Original representation, as entered. */
  readonly plate: string;
  readonly compact: string;
  readonly make: string | null;
  readonly model: string | null;
  readonly colour: string | null;
  readonly status: 'active' | 'blocked' | 'inactive';
  readonly statusReason: string | null;
  readonly presence: VehiclePresenceDto;
  readonly version: number;
  readonly associations: readonly VehicleAssociationDto[];
}

// ------------------------------------------------------------------ expected vehicles (PB-10-02)
export const AddVisitVehicleRequest = z.object({
  jurisdiction: Jurisdiction,
  plate: PlateText,
  make: optionalText(40),
  model: optionalText(40),
  colour: optionalText(40),
  driverParticipationId: Uuid.nullable().optional(),
  parkingRequested: z.boolean().default(false),
  accessibleParking: z.boolean().default(false),
});
export const UpdateVisitVehicleRequest = z.object({
  expectedVersion: z.number().int().min(1),
  driverParticipationId: Uuid.nullable().optional(),
  parkingRequested: z.boolean().optional(),
  accessibleParking: z.boolean().optional(),
});
export const RemoveVisitVehicleRequest = z.object({ reason: optionalText(500) });

export interface ParkingAllocationDto {
  readonly id: string;
  readonly zoneId: string;
  readonly zoneName: string;
  readonly zoneCode: string;
  readonly spaceId: string | null;
  readonly spaceCode: string | null;
  readonly gateLabel: string | null;
  readonly directions: string | null;
  readonly accessible: boolean;
  readonly from: string;
  readonly until: string;
}
export interface VisitVehicleDto {
  readonly id: string;
  readonly visitId: string;
  readonly vehicle: { readonly id: string; readonly jurisdiction: string; readonly plate: string; readonly make: string | null; readonly model: string | null; readonly colour: string | null };
  readonly driver: { readonly participationId: string; readonly displayName: string } | null;
  readonly parkingRequested: boolean;
  readonly accessibleParking: boolean;
  readonly status: 'expected' | 'removed';
  readonly version: number;
  readonly parking: ParkingAllocationDto | null;
  /** Independent vehicle presence; never the driver's presence. */
  readonly presence: VehiclePresenceDto;
  /** Registry status only for gate and registry roles; null for hosts. */
  readonly registryStatus: 'active' | 'blocked' | 'inactive' | null;
}
export interface VisitVehiclesResponse {
  readonly items: readonly VisitVehicleDto[];
  readonly canManage: boolean;
  readonly canAllocate: boolean;
  readonly jurisdictions: readonly string[];
}

// ------------------------------------------------------------------ parking (PB-10-03, PB-10-04)
export const CreateZoneRequest = z.object({
  code: Code,
  name: trimmed(120),
  capacity: z.number().int().min(0).max(100_000),
  accessibleCapacity: z.number().int().min(0).max(100_000).default(0),
  gateLabel: optionalText(80),
  directions: optionalText(1000),
});
export const UpdateZoneRequest = z.object({
  expectedVersion: z.number().int().min(1),
  name: trimmed(120).optional(),
  capacity: z.number().int().min(0).max(100_000).optional(),
  accessibleCapacity: z.number().int().min(0).max(100_000).optional(),
  gateLabel: optionalText(80),
  directions: optionalText(1000),
  status: z.enum(['active', 'inactive']).optional(),
});
export const CreateSpaceRequest = z.object({ code: Code, accessible: z.boolean().default(false) });
export const UpdateSpaceRequest = z.object({ expectedVersion: z.number().int().min(1), accessible: z.boolean().optional(), status: z.enum(['active', 'inactive']).optional() });
export const AllocateParkingRequest = z.object({ zoneId: Uuid, spaceId: Uuid.nullable().optional(), accessible: z.boolean().optional() });
export const ReleaseParkingRequest = z.object({ reason: trimmed(500) });
export const ParkingQuery = z.object({ siteId: Uuid, from: IsoDateTime.optional(), until: IsoDateTime.optional() });

export interface ParkingSpaceDto {
  readonly id: string;
  readonly code: string;
  readonly accessible: boolean;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  /** Whether an active allocation overlaps the queried period. */
  readonly taken: boolean;
}
export interface ParkingZoneDto {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly capacity: number;
  readonly accessibleCapacity: number;
  readonly gateLabel: string | null;
  readonly directions: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  /** Active allocations overlapping the queried period (conservative). */
  readonly allocated: number;
  readonly free: number;
  readonly spaces: readonly ParkingSpaceDto[];
}
export interface ParkingResponse {
  readonly siteId: string;
  readonly from: string;
  readonly until: string;
  readonly exactSpaces: boolean;
  readonly zones: readonly ParkingZoneDto[];
  readonly allocations: ReadonlyArray<{ readonly visitVehicleId: string; readonly plate: string; readonly visitReference: string; readonly allocation: ParkingAllocationDto }>;
}

/** Visitor-pass projection (PB-10-04): only the visitor's own current allocation and directions. */
export interface PassParkingDto {
  readonly plate: string;
  readonly zoneName: string;
  readonly spaceCode: string | null;
  readonly gateLabel: string | null;
  readonly directions: string | null;
  readonly accessible: boolean;
  readonly from: string;
  readonly until: string;
}

// ------------------------------------------------------------------ gate (PB-10-08 … PB-10-15)
export type GateOutcomeDto = 'clear' | 'blocked' | 'unresolved';
export interface GateEvaluationDto {
  readonly outcome: GateOutcomeDto;
  readonly blockers: readonly string[];
  readonly unknowns: readonly string[];
  readonly warnings: readonly string[];
  readonly window: { readonly start: string; readonly end: string } | null;
  /** Server-signed fingerprint of the facts and the evaluation time. */
  readonly token: string;
  readonly evaluatedAt: string;
  /** After this instant the evaluation cannot be applied: review again (PB-10-15). */
  readonly expiresAt: string;
}
export type GateFactState = 'ok' | 'blocked' | 'unknown' | 'info' | 'not_available';
export interface GateFactDto {
  readonly key: 'approval' | 'host_confirmation' | 'window' | 'identity' | 'credential' | 'permit' | 'screening' | 'zone' | 'parking' | 'plate_read' | 'vehicle_presence' | 'vehicle_status';
  readonly state: GateFactState;
  /** Catalogue key describing the value (e.g. state.approval.approved). */
  readonly value: string;
  readonly source: 'passage' | 'anpr' | 'module_pending' | 'policy';
  readonly at: string | null;
}
export interface GateSubjectDto {
  readonly kind: 'vehicle' | 'person';
  readonly visitVehicleId: string | null;
  readonly participationId: string | null;
  readonly vehicleId: string | null;
  readonly plate: string | null;
  readonly jurisdiction: string | null;
  /** Minimal person facts: display name and company only (PB-10-08). */
  readonly driverName: string | null;
  readonly organisation: string | null;
  readonly hostName: string | null;
  readonly visitReference: string | null;
  readonly destination: string | null;
  readonly timeZone: string;
}
export interface GateDecisionDto {
  readonly id: string;
  readonly kind: 'permit_entry' | 'deny';
  /** 'restricted' when the reason is sensitive and the reader lacks gate.restricted.read. */
  readonly reason: string | null;
  readonly visitorMessage: string | null;
  readonly nextStep: string | null;
  readonly note: string | null;
  /** Present only for readers with gate.restricted.read. */
  readonly restrictedNote: string | null;
  readonly decidedAt: string;
  readonly decidedBy: string | null;
  readonly gateLabel: string | null;
}
export interface VehicleMovementDto {
  readonly id: string;
  readonly kind: 'entry' | 'exit' | 'void';
  readonly observedAt: string;
  readonly recordedAt: string;
  readonly method: string;
  readonly recordedBy: string | null;
  readonly gateLabel: string | null;
  readonly voidsId: string | null;
  readonly voided: boolean;
  readonly note: string | null;
}
export interface GateTaskDto {
  readonly id: string;
  readonly kind: string;
  readonly status: 'open' | 'resolved';
  readonly reason: string | null;
  readonly subject: string | null;
  readonly participationId: string | null;
  readonly visitVehicleId: string | null;
  readonly vehicleId: string | null;
  readonly ownerName: string | null;
  readonly createdAt: string;
  readonly history: ReadonlyArray<{ readonly kind: string; readonly note: string | null; readonly at: string; readonly by: string | null }>;
}
export interface GateCommandDto {
  readonly id: string;
  readonly gateLabel: string;
  readonly status: 'queued' | 'dispatched' | 'acknowledged' | 'confirmed' | 'failed' | 'unknown' | 'reconciling';
  readonly statusReason: string | null;
  readonly requestedAt: string;
  readonly outcomeAt: string | null;
  readonly version: number;
}
export interface GateReviewResponse {
  readonly subject: GateSubjectDto;
  readonly direction: 'entry' | 'exit';
  readonly evaluation: GateEvaluationDto;
  readonly facts: readonly GateFactDto[];
  readonly suggestedReason: string | null;
  readonly vehiclePresence: VehiclePresenceDto | null;
  readonly parking: ParkingAllocationDto | null;
  readonly decisions: readonly GateDecisionDto[];
  readonly movements: readonly VehicleMovementDto[];
  readonly tasks: readonly GateTaskDto[];
  readonly commands: readonly GateCommandDto[];
}

export interface GateQueueItem {
  readonly visitVehicleId: string;
  readonly vehicleId: string;
  readonly plate: string;
  readonly jurisdiction: string;
  readonly driverName: string | null;
  readonly organisation: string | null;
  readonly hostName: string;
  readonly visitReference: string;
  readonly window: { readonly start: string; readonly end: string } | null;
  readonly timeZone: string;
  readonly presence: VehiclePresenceDto;
  readonly parking: { readonly zoneName: string; readonly spaceCode: string | null } | null;
  /** Quick pre-check from current facts; the review re-evaluates before any decision. */
  readonly hint: GateOutcomeDto;
}
export interface OnsiteVehicleDto {
  readonly vehicleId: string;
  readonly plate: string;
  readonly jurisdiction: string;
  readonly since: string | null;
  readonly visitVehicleId: string | null;
  readonly overstay: boolean;
  /** The linked driver's own presence, shown side by side, never merged. */
  readonly driverPresence: string | null;
}
export interface AnprReadDto {
  readonly id: string;
  readonly sourceId: string;
  readonly gateLabel: string | null;
  readonly direction: 'entry' | 'exit' | 'unknown';
  readonly observedAt: string;
  readonly outcome: 'matched' | 'ambiguous' | 'no_match' | 'low_confidence' | 'unreadable';
  readonly confidence: number | null;
  /** Full plate only when exactly one scoped vehicle matched; otherwise masked. */
  readonly plate: string | null;
  readonly duplicateOf: string | null;
}
export interface GateExceptionDto {
  readonly id: string;
  readonly kind: 'ambiguous' | 'low_confidence' | 'unreadable' | 'no_match';
  readonly status: 'open' | 'resolved' | 'dismissed';
  readonly read: AnprReadDto;
  /** Masked candidates: enough to choose, not a directory (PB-10-07). */
  readonly candidates: ReadonlyArray<{ readonly vehicleId: string; readonly maskedPlate: string; readonly jurisdiction: string; readonly expectedToday: boolean }>;
  readonly corrected: { readonly jurisdiction: string | null; readonly plate: string | null } | null;
  readonly resolvedVehicleId: string | null;
  readonly version: number;
  readonly createdAt: string;
}
export interface GateQueueResponse {
  readonly siteId: string;
  readonly date: string;
  readonly timeZone: string;
  readonly asOf: string;
  readonly counts: { readonly expected: number; readonly onsite: number; readonly exceptions: number; readonly tasks: number };
  readonly expected: readonly GateQueueItem[];
  readonly onsite: readonly OnsiteVehicleDto[];
  readonly exceptions: readonly GateExceptionDto[];
  readonly tasks: readonly GateTaskDto[];
  readonly reads: readonly AnprReadDto[];
  readonly commands: readonly GateCommandDto[];
  readonly policy: {
    readonly offline: GateRulesDto['offline'];
    readonly evaluationMaxAgeSeconds: number;
    readonly photo: GateRulesDto['photo'];
    readonly barrierAvailable: boolean;
    readonly jurisdictions: readonly string[];
    readonly categories: ReadonlyArray<{ readonly code: string; readonly label: string }>;
  };
}
export const GateQueueQuery = z.object({ siteId: Uuid, date: LocalDate.optional() });

export const GateLookupRequest = z.object({ siteId: Uuid, query: z.string().trim().min(2, 'v.tooShort').max(300, 'v.tooLong'), jurisdiction: Jurisdiction.optional() });
export interface GateCandidateDto {
  readonly kind: 'vehicle' | 'person';
  readonly visitVehicleId: string | null;
  readonly participationId: string | null;
  readonly vehicleId: string | null;
  /** Full plate for an exact expected match; masked otherwise. */
  readonly plate: string | null;
  readonly jurisdiction: string | null;
  readonly name: string | null;
  readonly visitReference: string | null;
  readonly window: { readonly start: string; readonly end: string } | null;
  readonly timeZone: string;
  readonly match: 'exact' | 'near';
}
export interface GateLookupResponse {
  readonly items: readonly GateCandidateDto[];
  /** A registered vehicle with no visit in range: present but not expected (never permission). */
  readonly registeredNotExpected: boolean;
  readonly rejected: 'link_invalid' | null;
}

export const GateReviewQuery = z
  .object({
    siteId: Uuid,
    direction: z.enum(['entry', 'exit']).default('entry'),
    visitVehicleId: Uuid.optional(),
    participationId: Uuid.optional(),
    vehicleId: Uuid.optional(),
    anprReadId: Uuid.optional(),
  })
  .refine((s) => !!(s.visitVehicleId || s.participationId || s.vehicleId || s.anprReadId), { message: 'v.required', path: ['visitVehicleId'] });

const Subject = z
  .object({ visitVehicleId: Uuid.optional(), participationId: Uuid.optional(), vehicleId: Uuid.optional(), anprReadId: Uuid.optional() })
  .refine((s) => !!(s.visitVehicleId || s.participationId || s.vehicleId || s.anprReadId), { message: 'v.required' });
export const DENIAL_REASON_VALUES = [
  'not_expected', 'outside_window', 'approval_pending', 'approval_rejected', 'visit_cancelled', 'vehicle_not_registered',
  'vehicle_blocked', 'identity_unverified', 'parking_full', 'host_unavailable', 'screening_hold', 'safety', 'other',
] as const;
export const NEXT_STEP_VALUES = ['contact_host', 'return_later', 'visit_reception', 'register_vehicle', 'leave_site', 'supervisor_review'] as const;
export const GateDecisionRequest = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('permit_entry'),
    siteId: Uuid,
    subject: Subject,
    evaluationToken: z.string().min(10).max(120),
    evaluatedAt: IsoDateTime,
    gateLabel: optionalText(80),
    note: optionalText(500),
  }),
  z.object({
    kind: z.literal('deny'),
    siteId: Uuid,
    subject: Subject,
    reason: z.enum(DENIAL_REASON_VALUES),
    nextStep: z.enum(NEXT_STEP_VALUES),
    note: optionalText(500),
    restrictedNote: optionalText(1000),
    gateLabel: optionalText(80),
  }),
]);
export type GateDecisionRequest = z.infer<typeof GateDecisionRequest>;

export const MOVEMENT_METHODS = ['guard_visual', 'anpr_confirmed', 'manual_plate', 'offline_reconciled'] as const;
export const RecordMovementRequest = z
  .object({
    siteId: Uuid,
    kind: z.enum(['entry', 'exit']),
    vehicleId: Uuid.optional(),
    visitVehicleId: Uuid.optional(),
    /** For a vehicle not yet in the site registry (a minimal record is created, PD-164). */
    plate: PlateText.optional(),
    jurisdiction: Jurisdiction.optional(),
    method: z.enum(MOVEMENT_METHODS),
    observedAt: IsoDateTime.optional(),
    gateLabel: optionalText(80),
    anprReadId: Uuid.optional(),
    note: optionalText(500),
  })
  .refine((m) => !!(m.vehicleId || m.visitVehicleId || (m.plate && m.jurisdiction)), { message: 'v.vehicleRequired', path: ['plate'] });
export const VoidMovementRequest = z.object({ reason: trimmed(500) });
export interface MovementResult {
  readonly movement: VehicleMovementDto;
  readonly vehicleId: string;
  readonly presence: VehiclePresenceDto;
  readonly warnings: readonly string[];
  /** An exception opened by this observation (e.g. entry without a permitted-entry decision). */
  readonly taskKind: string | null;
}

export const EscalationRequest = z
  .object({
    siteId: Uuid,
    kind: z.enum(['host_contact', 'supervisor_escalation']),
    participationId: Uuid.optional(),
    visitVehicleId: Uuid.optional(),
    reason: trimmed(500),
    notifyHost: z.boolean().default(false),
  })
  .refine((e) => !!(e.participationId || e.visitVehicleId), { message: 'v.required', path: ['participationId'] });
export const ResolveGateTaskRequest = z.object({ resolution: trimmed(500) });

export const GateHostLookupRequest = z.object({ siteId: Uuid, query: z.string().trim().max(100, 'v.tooLong') });
export interface GateHostCandidate {
  readonly id: string;
  readonly displayName: string;
  readonly department: string | null;
}
export const GateRegistrationRequest = z.object({
  siteId: Uuid,
  hostId: Uuid,
  categoryCode: z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey'),
  purpose: optionalText(500),
  until: LocalTime.optional(),
  participant: z.object({ givenName: trimmed(100), familyName: optionalText(100), organisation: optionalText(200), phone: z.string().trim().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').optional() }),
  vehicle: z.object({ jurisdiction: Jurisdiction, plate: PlateText }).optional(),
});
export interface GateRegistrationResponse {
  readonly visitId: string;
  readonly participationId: string;
  readonly visitVehicleId: string | null;
  readonly reference: string;
  readonly taskId: string;
}

export const PhotoQuery = z
  .object({
    siteId: Uuid,
    subject: z.enum(['vehicle', 'person']),
    purpose: z.enum(PHOTO_PURPOSES),
    vehicleId: Uuid.optional(),
    participationId: Uuid.optional(),
    noticeGiven: z.literal('true', { message: 'v.photoNotice' }),
  })
  .refine((q) => (q.subject === 'vehicle' ? !!q.vehicleId : !!q.participationId), { message: 'v.required', path: ['subject'] });
export interface GatePhotoResponse {
  readonly id: string;
  readonly capturedAt: string;
  readonly retentionUntil: string;
}
export const PhotoDeclinedRequest = z
  .object({
    siteId: Uuid,
    reason: z.enum(['camera_unavailable', 'subject_declined', 'not_permitted']),
    vehicleId: Uuid.optional(),
    participationId: Uuid.optional(),
    note: optionalText(500),
  })
  .refine((q) => !!(q.vehicleId || q.participationId), { message: 'v.required', path: ['participationId'] });

export const BarrierCommandRequest = z.object({ gateDecisionId: Uuid, gateLabel: GateLabel });
export const ReconcileCommandRequest = z.object({ outcome: z.enum(['confirmed', 'failed']), note: trimmed(500) });

// ------------------------------------------------------------------ ANPR (PB-10-06, PB-10-07)
export const SimulatedAnprEvent = z.object({
  sourceId: z.string().trim().min(1).max(80),
  eventId: z.string().trim().min(1).max(120).optional(),
  plate: z.string().trim().max(40).nullable(),
  jurisdiction: Jurisdiction.nullable().optional(),
  confidence: z.number().min(0).max(1).nullable().optional(),
  observedAt: IsoDateTime.optional(),
  direction: z.enum(['entry', 'exit', 'unknown']).default('unknown'),
  gateLabel: optionalText(80),
});
export const AnprIngestRequest = z.object({ adapter: z.enum(['simulator']), events: z.array(z.unknown()).min(1).max(50) });
export interface AnprIngestResponse {
  readonly results: ReadonlyArray<{ readonly readId: string; readonly outcome: string; readonly duplicate: boolean; readonly exceptionId: string | null }>;
}
export const ResolveExceptionRequest = z
  .object({
    expectedVersion: z.number().int().min(1),
    action: z.enum(['match', 'dismiss']),
    vehicleId: Uuid.optional(),
    correctedJurisdiction: Jurisdiction.optional(),
    correctedPlate: PlateText.optional(),
    note: trimmed(500),
  })
  .refine((r) => r.action === 'dismiss' || !!r.vehicleId || !!(r.correctedPlate && r.correctedJurisdiction), { message: 'v.vehicleRequired', path: ['correctedPlate'] });

// ------------------------------------------------------------------ plate lookup for other desks (PB-06-02)
export const PlateLookupRequest = z.object({ siteId: Uuid, plate: PlateText, jurisdiction: Jurisdiction.optional() });
export interface PlateLookupResponse {
  readonly items: ReadonlyArray<{ readonly participationId: string | null; readonly visitId: string; readonly visitVehicleId: string; readonly reference: string; readonly plate: string; readonly jurisdiction: string; readonly driverName: string | null }>;
}
